<template>
  <div class="table-container">
    <a-table
      :columns="columns"
      :data-source="dataList"
      :loading="loading"
      :pagination="pagination"
      :row-key="(record) => record._id"
      :custom-row="customRow"
      @change="handleTableChange"
    >
      <!-- No 컬럼 -->
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'index'">
          {{ record.index }}
        </template>

        <!-- 회사명 컬럼 -->
        <template v-else-if="column.key === 'name'">
          <span class="company-name">{{ record.name }}</span>
          <a-tag v-if="record.type" size="small" class="type-tag">
            {{ getTypeLabel(record.type) }}
          </a-tag>
        </template>

        <!-- 채널ID 컬럼 - 신규/기존 구조 모두 지원 -->
        <template v-else-if="column.key === 'kakaoChannels'">
          <!-- 신규 구조: kakaoChannels 배열 -->
          <div v-if="record.kakaoChannels?.length > 0" class="channels-list">
            <a-tag
              v-for="channel in record.kakaoChannels"
              :key="channel.channelId"
              color="blue"
              class="channel-tag clickable"
              @click.stop="handleChannelClick(record, channel.channelId)"
            >
              {{ channel.channelId }}
            </a-tag>
          </div>
          <!-- 기존 구조: channelId 단일 값 -->
          <span
            v-else-if="record.channelId"
            class="channel-id clickable"
            @click.stop="handleChannelClick(record, record.channelId)"
          >{{ record.channelId }}</span>
          <span v-else>-</span>
        </template>

        <!-- 관리자 연락처 컬럼 - 신규/기존 구조 모두 지원 -->
        <template v-else-if="column.key === 'admins'">
          <div v-if="getAllAdmins(record).length > 0" class="admins-list">
            <span>{{ formatPhone(getAllAdmins(record)[0]) }}</span>
            <span v-if="getAllAdmins(record).length > 1" class="admins-more">
              외 {{ getAllAdmins(record).length - 1 }}명
            </span>
          </div>
          <span v-else>-</span>
        </template>

        <!-- 상태 컬럼 -->
        <template v-else-if="column.key === 'status'">
          <a-tag :color="getStatusColor(record.status)">
            {{ getStatusLabel(record.status) }}
          </a-tag>
        </template>

        <!-- 신청일 컬럼 -->
        <template v-else-if="column.key === 'requestedAt'">
          {{ formatDate(record.requestedAt) }}
        </template>

        <!-- 승인일 컬럼 -->
        <template v-else-if="column.key === 'connectedAt'">
          {{ record.connectedAt ? formatDate(record.connectedAt) : '-' }}
        </template>
      </template>
    </a-table>
  </div>
</template>

<script>
import { COUNSELOR_STATUS_LABEL, COUNSELOR_STATUS_COLOR } from '../constants';

export default {
  name: 'CounselorTable',
  props: {
    dataList: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['row-click', 'channel-click', 'table-change'],
  setup(props, { emit }) {
    const columns = [
      {
        title: 'No',
        key: 'index',
        width: 60,
        align: 'center',
      },
      {
        title: '회사명',
        key: 'name',
        dataIndex: 'name',
        width: 200,
      },
      {
        title: '채널ID',
        key: 'kakaoChannels',
        dataIndex: 'kakaoChannels',
        width: 200,
      },
      {
        title: '관리자 연락처',
        key: 'admins',
        width: 180,
      },
      {
        title: '상태',
        key: 'status',
        dataIndex: 'status',
        width: 100,
        align: 'center',
      },
      {
        title: '신청일',
        key: 'requestedAt',
        dataIndex: 'requestedAt',
        width: 120,
        align: 'center',
      },
      {
        title: '승인일',
        key: 'connectedAt',
        dataIndex: 'connectedAt',
        width: 120,
        align: 'center',
      },
    ];

    const getStatusLabel = (status) => {
      return COUNSELOR_STATUS_LABEL[status] || status;
    };

    const getStatusColor = (status) => {
      return COUNSELOR_STATUS_COLOR[status] || 'default';
    };

    const getTypeLabel = (type) => {
      const typeLabels = {
        HOSPITAL: '병원',
        MANUFACTURER: '제조사',
        STUDIO: '스튜디오',
      };
      return typeLabels[type] || type;
    };

    const formatPhone = (phone) => {
      if (!phone) return '-';
      // 010-1234-5678 형식으로 변환
      return phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    };

    // 모든 채널의 관리자 전화번호 추출 (중복 제거, 다양한 구조 지원)
    const getAllAdmins = (record) => {
      // 구조 1: record.admins 배열 (직접 admins가 있는 경우)
      if (record.admins?.length > 0) {
        const phones = record.admins.map(a => a.phone || a);
        return [...new Set(phones)];
      }
      // 구조 2: kakaoChannels 배열 내 admins
      if (record.kakaoChannels?.length > 0) {
        const phones = record.kakaoChannels
          .flatMap(ch => ch.admins?.map(a => a.phone || a) || []);
        return [...new Set(phones)]; // 중복 제거
      }
      // 구조 3: adminPhone 단일 값 (기존 구조)
      if (record.adminPhone) {
        return [record.adminPhone];
      }
      // 구조 4: adminPhones 배열
      if (record.adminPhones?.length > 0) {
        return [...new Set(record.adminPhones)];
      }
      return [];
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const customRow = (record) => {
      return {
        onClick: () => {
          emit('row-click', record);
        },
        style: { cursor: 'pointer' },
      };
    };

    const handleChannelClick = (record, channelId) => {
      emit('channel-click', record, channelId);
    };

    const handleTableChange = (pag, filters, sorter) => {
      emit('table-change', pag, filters, sorter);
    };

    return {
      columns,
      getStatusLabel,
      getStatusColor,
      getTypeLabel,
      formatPhone,
      formatDate,
      getAllAdmins,
      customRow,
      handleChannelClick,
      handleTableChange,
    };
  },
};
</script>

<style scoped lang="scss">
.table-container {
  padding: 16px;
}

.company-name {
  font-weight: 500;
  margin-right: 8px;
}

.type-tag {
  font-size: 11px;
}

.channel-id {
  color: #1890ff;
  font-family: monospace;
}

.channels-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.channel-tag {
  margin: 0;
  font-family: monospace;
}

.clickable {
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.admins-list {
  font-size: 13px;
  color: #333;
}

.admins-more {
  color: #999;
  margin-left: 4px;
}

:deep(.ant-table-row) {
  &:hover {
    background-color: #fafafa;
  }
}
</style>
